import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, HostBinding, Input } from '@angular/core';

export type BkButtonKind = 'filled' | 'outlined' | 'text';
export type BkButtonColor = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
export type BkButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type BkButtonShape = 'rounded' | 'pill';

/**
 * Applies the design-system button styling to a native `<button>` or `<a>`.
 * Native semantics (routerLink, href, type=submit, disabled, form association,
 * middle-click) are preserved because the host element is unchanged.
 *
 * Usage: `<button bkButton color="primary" (click)="..">Save</button>`
 *        `<a bkButton color="success" routerLink="..">Back</a>`
 */
@Directive({
  selector: '[bkButton]',
})
export class BkButtonDirective {
  @Input() kind: BkButtonKind = 'filled';
  @Input() color: BkButtonColor = 'primary';
  @Input() size: BkButtonSize = 'md';
  @Input() shape: BkButtonShape = 'rounded';

  @Input()
  get block(): boolean { return this._block; }
  set block(value: BooleanInput) { this._block = coerceBooleanProperty(value); }
  private _block = false;

  @Input()
  get iconOnly(): boolean { return this._iconOnly; }
  set iconOnly(value: BooleanInput) { this._iconOnly = coerceBooleanProperty(value); }
  private _iconOnly = false;

  @Input()
  get loading(): boolean { return this._loading; }
  set loading(value: BooleanInput) { this._loading = coerceBooleanProperty(value); }
  private _loading = false;

  @HostBinding('class.bk-btn') readonly hostBtnClass = true;
  @HostBinding('attr.data-bk-kind') get kindAttr(): string { return this.kind; }
  @HostBinding('attr.data-bk-color') get colorAttr(): string { return this.color; }
  @HostBinding('attr.data-bk-size') get sizeAttr(): string { return this.size; }
  @HostBinding('attr.data-bk-shape') get shapeAttr(): string { return this.shape; }
  @HostBinding('class.bk-btn--block') get blockClass(): boolean { return this._block; }
  @HostBinding('class.bk-btn--icon') get iconClass(): boolean { return this._iconOnly; }
  @HostBinding('class.bk-btn--loading') get loadingClass(): boolean { return this._loading; }
  @HostBinding('attr.aria-busy') get ariaBusy(): string | null { return this._loading ? 'true' : null; }
}
